import { useQuery } from "@tanstack/react-query";
import { supabase } from "../supabase";

// Types
export interface ImpactStats {
  total_food_saved: number;
  total_meals_shared: number;
  total_volunteers: number;
  total_donors: number;
  total_recipients: number;
  co2_emissions_saved: number;
  waste_reduced_kg: number;
}

// Fetch impact statistics
const fetchImpactStats = async (): Promise<ImpactStats> => {
  // Get counts from different tables
  const [
    { count: foodListingsCount } = { count: 0 },
    { count: volunteersCount } = { count: 0 },
    { count: donorsCount } = { count: 0 },
    { count: recipientsCount } = { count: 0 },
  ] = await Promise.all([
    supabase
      .from("food_listings")
      .select("*", { count: "exact", head: true })
      .eq("status", "claimed"),
    supabase
      .from("users")
      .select("*", { count: "exact", head: true })
      .eq("role", "volunteer"),
    supabase
      .from("users")
      .select("*", { count: "exact", head: true })
      .eq("role", "donor"),
    supabase
      .from("users")
      .select("*", { count: "exact", head: true })
      .eq("role", "recipient"),
  ]);

  // Calculate derived stats
  const totalFoodSaved = foodListingsCount || 0;
  const totalMealsShared = Math.floor(totalFoodSaved * 2.5); // Assume 2.5 meals per food item
  const totalVolunteers = volunteersCount || 0;
  const totalDonors = donorsCount || 0;
  const totalRecipients = recipientsCount || 0;

  // Environmental impact calculations
  const co2EmissionsSaved = totalFoodSaved * 2.5; // kg CO2 per food item saved
  const wasteReducedKg = totalFoodSaved * 1.2; // kg waste reduced per food item

  return {
    total_food_saved: totalFoodSaved,
    total_meals_shared: totalMealsShared,
    total_volunteers: totalVolunteers,
    total_donors: totalDonors,
    total_recipients: totalRecipients,
    co2_emissions_saved: co2EmissionsSaved,
    waste_reduced_kg: wasteReducedKg,
  };
};

// Custom hook
export const useImpactStats = () => {
  return useQuery({
    queryKey: ["impact-stats"],
    queryFn: fetchImpactStats,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 30 * 60 * 1000, // 30 minutes
  });
};
